import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.silkspace.co/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Add News Ticker Service
export const newsTickerService = {
  // Get all news items
  getAll: () => 
    api.get('/news-ticker/').then(res => res.data),
  
  // Get single news item
  getNews: (id) => 
    api.get(`/news-ticker/${id}/`).then(res => res.data),
  
  // Create news item
  create: (data) => 
    api.post('/news-ticker/', data).then(res => res.data),
  
  // Update news item
  update: (id, data) => 
    api.patch(`/news-ticker/${id}/`, data).then(res => res.data),
  
  // Delete news item
  delete: (id) => 
    api.delete(`/news-ticker/${id}/`).then(res => res.data),
  
  // Get ticker status
  getStatus: () => 
    api.get('/news-ticker/status/').then(res => res.data),
  
  // Toggle ticker
  toggleTicker: (enable) => 
    api.post('/news-ticker/toggle/', { enable }).then(res => res.data),
  
  // Bulk update
  bulkUpdate: (data) => 
    api.post('/news-ticker/bulk-update/', data).then(res => res.data),
  
  // Get active news (public)
  getActiveNews: () => 
    api.get('/news-ticker/active/').then(res => res.data),
};

// Music Services - Complete Updated Version
export const musicService = {
  // Admin endpoints
  getAll: (params = {}) => 
    api.get('/music/', { params }).then(res => {
      // Handle different response formats
      if (res.data && Array.isArray(res.data)) {
        return res.data;
      } else if (res.data && res.data.results) {
        return res.data.results;
      }
      return res.data || [];
    }).catch(error => {
      console.error('Error fetching music:', error);
      return [];
    }),
  
  getPending: () => 
    api.get('/music/?status=pending').then(res => {
      if (res.data && Array.isArray(res.data)) {
        return res.data;
      } else if (res.data && res.data.results) {
        return res.data.results;
      }
      return res.data || [];
    }).catch(error => {
      console.error('Error fetching pending music:', error);
      return [];
    }),
  
  getGenres: async () => {
    try {
      const response = await api.get('/music-genres/');
      // Handle different response formats
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.results) {
        return response.data.results;
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching music genres:', error);
      // Return default genres if all endpoints fail
      return [
        { id: 1, name: 'Hip Hop' },
        { id: 2, name: 'Afrobeats' },
        { id: 3, name: 'Highlife' },
        { id: 4, name: 'Gospel' },
        { id: 5, name: 'R&B' },
        { id: 6, name: 'Pop' },
      ];
    }
  },
  
  getArtists: async () => {
    try {
      const response = await api.get('/music/artists/');
      // Handle different response formats
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.results) {
        return response.data.results;
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching artists:', error);
      return [];
    }
  },
  
  adminUploadMusic: async (musicData) => {
  console.log('🚀 Starting music upload with data:', musicData);
  
  try {
    // Function to fix URL protocol
    const fixUrlProtocol = (url) => {
      if (!url) return '';
      // If URL already has protocol, return as is
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      // Otherwise prepend https://
      return `https://${url}`;
    };

    // Clean and prepare data
    const payload = {
      title: musicData.title,
      description: musicData.description,
      artist_id: musicData.artist_id === 'new' ? 'new' : musicData.artist_id,
      genre: musicData.genre || null,
      price: parseFloat(musicData.price) || 0,
      // Fix URLs by ensuring they have https:// protocol
      cover_image_url: fixUrlProtocol(musicData.cover_image_url),
      preview_audio_url: fixUrlProtocol(musicData.preview_audio_url),
      full_audio_url: fixUrlProtocol(musicData.full_audio_url),
      duration: parseInt(musicData.duration) || 0,
      preview_duration: parseInt(musicData.preview_duration) || 90,
      bitrate: parseInt(musicData.bitrate) || 320,
      file_size: parseInt(musicData.fileSize || musicData.file_size) || 0,
      age_rating: musicData.age_rating || 'all',
      release_date: musicData.release_date,
      status: musicData.status || 'approved',
      is_coming_soon: Boolean(musicData.is_coming_soon),
    };

    // Handle new artist
    if (musicData.artist_id === 'new') {
      payload.new_artist = {
        name: musicData.artist_name,
        email: musicData.artist_email,
        bio: musicData.artist_bio || '',
        profile_image_url: musicData.artist_profile_image_url || '',
      };
    }

    // Handle featuring artists – backend expects integer PKs
    if (musicData.featuring && Array.isArray(musicData.featuring)) {
      payload.featuring = musicData.featuring
        .map(id => { const n = parseInt(id, 10); return isNaN(n) ? null : n; })
        .filter(Boolean);
    } else {
      payload.featuring = [];
    }

    console.log('📤 Final payload:', payload);

    const response = await api.post('/music/', payload);
    console.log('✅ Music upload successful:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Music upload failed:', error);
    
    if (error.response) {
      console.error('Server error response:', error.response.data);
      console.error('Server error status:', error.response.status);
      
      let errorMessage = 'Upload failed';
      if (error.response.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'object') {
          errorMessage = JSON.stringify(error.response.data, null, 2);
        }
      }
      
      throw new Error(errorMessage);
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
},

adminUpdateMusic: async (musicId, musicData) => {
  console.log('🔄 Starting music update with data:', musicData);
  
  try {
    // Function to fix URL protocol
    const fixUrlProtocol = (url) => {
      if (!url) return '';
      // If URL already has protocol, return as is
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      // Otherwise prepend https://
      return `https://${url}`;
    };

    // Clean and prepare data
    const payload = {
      title: musicData.title,
      description: musicData.description,
      artist_id: musicData.artist_id === 'new' ? 'new' : musicData.artist_id,
      genre: musicData.genre || null,
      price: parseFloat(musicData.price) || 0,
      // Fix URLs by ensuring they have https:// protocol
      cover_image_url: fixUrlProtocol(musicData.cover_image_url),
      preview_audio_url: fixUrlProtocol(musicData.preview_audio_url),
      full_audio_url: fixUrlProtocol(musicData.full_audio_url),
      duration: parseInt(musicData.duration) || 0,
      preview_duration: parseInt(musicData.preview_duration) || 90,
      bitrate: parseInt(musicData.bitrate) || 320,
      file_size: parseInt(musicData.file_size || musicData.fileSize) || 0,
      age_rating: musicData.age_rating || 'all',
      release_date: musicData.release_date,
      status: musicData.status || 'approved',
      is_coming_soon: Boolean(musicData.is_coming_soon),
    };

    // Handle new artist
    if (musicData.artist_id === 'new' && musicData.artist_name && musicData.artist_email) {
      payload.new_artist = {
        name: musicData.artist_name,
        email: musicData.artist_email,
        bio: musicData.artist_bio || ''
      };
    }

    // Handle featuring artists – backend expects integer PKs
    if (musicData.featuring && Array.isArray(musicData.featuring)) {
      payload.featuring = musicData.featuring
        .map(id => { const n = parseInt(id, 10); return isNaN(n) ? null : n; })
        .filter(Boolean);
    } else {
      payload.featuring = [];
    }

    console.log('📤 Update payload:', payload);

    // Use PATCH so read-only fields (artist, svp_id…) don't cause 400 errors
    const response = await api.patch(`/music/${musicId}/`, payload);
    console.log('✅ Music update successful:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Music update failed:', error);
    
    if (error.response) {
      console.error('Server error response:', error.response.data);
      console.error('Server error status:', error.response.status);
      
      let errorMessage = 'Update failed';
      if (error.response.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'object') {
          errorMessage = JSON.stringify(error.response.data, null, 2);
        }
      }
      
      throw new Error(errorMessage);
    } else {
      throw new Error(`Network error: ${error.message}`);
    }
  }
},
  
  adminUpdateStatus: async (musicId, status, reason = '') => {
    try {
      const data = { status };
      if (reason) {
        data.reason = reason;
      }
      
      const response = await api.patch(`/music/${musicId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating music status:', error);
      throw error;
    }
  },
  
  getMusic: (id) => 
    api.get(`/music/${id}/`).then(res => res.data).catch(error => {
      console.error(`Error fetching music ${id}:`, error);
      throw error;
    }),
  
  updateMusic: async (musicId, data) => {
  try {
    // Function to fix URL protocol
    const fixUrlProtocol = (url) => {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://${url}`;
    };

    // Fix URLs in the data
    const urlFields = ['cover_image_url', 'preview_audio_url', 'full_audio_url'];
    const updatedData = { ...data };
    
    urlFields.forEach(field => {
      if (updatedData[field]) {
        updatedData[field] = fixUrlProtocol(updatedData[field]);
      }
    });

    const response = await api.put(`/music/${musicId}/`, updatedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating music ${musicId}:`, error);
    throw error;
  }
},

uploadMusicFile: async (musicId, file, fileType) => {
  console.log(`Uploading ${fileType} for music ${musicId}:`, file.name);
  
  try {
    let uploadData;
    
    if (fileType === 'cover') {
      uploadData = await digitalOceanService.generateMusicCoverUploadURL({
        filename: file.name
      });
    } else if (fileType === 'audio') {
      uploadData = await digitalOceanService.generateMusicAudioUploadURL({
        filename: file.name,
        type: 'full' // or 'preview' - you might want to differentiate
      });
    } else if (fileType === 'preview') {
      uploadData = await digitalOceanService.generateMusicAudioUploadURL({
        filename: file.name,
        type: 'preview'
      });
    }
    
    if (!uploadData.success) {
      throw new Error(uploadData.error || 'Failed to get upload URL');
    }

    await digitalOceanService.uploadToSpaces(
      uploadData.upload_url,
      uploadData.fields,
      file
    );

    return uploadData.public_url;
    
  } catch (error) {
    console.error(`Error uploading ${fileType}:`, error);
    throw error;
  }
},
  
  deleteMusic: async (musicId) => {
    try {
      const response = await api.delete(`/music/${musicId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting music ${musicId}:`, error);
      throw error;
    }
  },
  
  // Artist management
  createArtist: (data) => 
    api.post('/music/artists/', data).then(res => res.data).catch(error => {
      console.error('Error creating artist:', error);
      throw error;
    }),
  
  updateArtist: (artistId, data) => 
    api.put(`/music/artists/${artistId}/`, data).then(res => res.data).catch(error => {
      console.error(`Error updating artist ${artistId}:`, error);
      throw error;
    }),
  
  // Public endpoints
  getNewMusic: () => 
    api.get('/music/new/').then(res => res.data).catch(error => {
      console.error('Error fetching new music:', error);
      return [];
    }),
  
  getCremeMusic: () => 
    api.get('/music/creme/').then(res => res.data).catch(error => {
      console.error('Error fetching creme music:', error);
      return [];
    }),
  
  playPreview: (musicId) => 
    api.post(`/music/${musicId}/play-preview/`).then(res => res.data).catch(error => {
      console.error(`Error playing preview for ${musicId}:`, error);
      return { error: error.message };
    }),
  
  purchase: (musicId) => 
    api.post(`/music/${musicId}/purchase/`).then(res => res.data).catch(error => {
      console.error(`Error purchasing music ${musicId}:`, error);
      throw error;
    }),
};

// Events Services - UPDATED
export const eventsService = {
  // Admin endpoints
  getAll: (params = {}) => 
    api.get('/admin/events/', { params }).then(res => {
      // Handle different response formats
      if (res.data && Array.isArray(res.data)) {
        return res.data;
      } else if (res.data && res.data.results) {
        return res.data.results;
      }
      return res.data || [];
    }).catch(error => {
      console.error('Error fetching events:', error);
      return [];
    }),
  
  getPending: () => 
    api.get('/admin/events/?status=pending').then(res => {
      if (res.data && Array.isArray(res.data)) {
        return res.data;
      } else if (res.data && res.data.results) {
        return res.data.results;
      }
      return res.data || [];
    }).catch(error => {
      console.error('Error fetching pending events:', error);
      return [];
    }),
  
  getEventTypes: async () => {
    try {
      const response = await api.get('/events/types/');
      // Handle different response formats
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.results) {
        return response.data.results;
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching event types:', error);
      return [
        { id: 1, name: 'Concert' },
        { id: 2, name: 'Festival' },
        { id: 3, name: 'Workshop' },
        { id: 4, name: 'Meetup' },
        { id: 5, name: 'Virtual' },
      ];
    }
  },
  
  adminCreateEvent: async (eventData) => {
    console.log('🎭 Starting event creation with data:', eventData);
    
    try {
      // Clean and prepare data
      const payload = {
        title: eventData.title,
        description: eventData.description,
        short_description: eventData.short_description || '',
        event_type: eventData.event_type || null,
        venue_name: eventData.venue_name,
        venue_address: eventData.venue_address,
        city: eventData.city,
        country: eventData.country || 'Ghana',
        is_virtual: Boolean(eventData.is_virtual),
        virtual_link: eventData.virtual_link || '',
        start_date: eventData.start_date,
        end_date: eventData.end_date || null,
        start_time: eventData.start_time,
        end_time: eventData.end_time || null,
        timezone: eventData.timezone || 'Africa/Accra',
        ticket_type: eventData.ticket_type || 'paid',
        price: parseFloat(eventData.price) || 0,
        early_bird_price: eventData.early_bird_price ? parseFloat(eventData.early_bird_price) : null,
        early_bird_end: eventData.early_bird_end || null,
        vip_price: eventData.vip_price ? parseFloat(eventData.vip_price) : null,
        capacity: parseInt(eventData.capacity) || 0,
        preview_video_url: eventData.preview_video_url || '',
        map_embed: eventData.map_embed || '',
        cover_image_url: eventData.cover_image_url,
        status: eventData.status || 'approved',
      };

      // Handle hosts
      if (eventData.hosts && Array.isArray(eventData.hosts)) {
        payload.hosts = eventData.hosts.map(id => parseInt(id)).filter(id => !isNaN(id));
      }

      // Handle featured artists
      if (eventData.featured_artists && Array.isArray(eventData.featured_artists)) {
        payload.featured_artists = eventData.featured_artists.map(id => parseInt(id)).filter(id => !isNaN(id));
      }

      // Handle tags
      if (eventData.tags) {
        if (typeof eventData.tags === 'string') {
          payload.tags = eventData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        } else if (Array.isArray(eventData.tags)) {
          payload.tags = eventData.tags;
        }
      }

      // Handle social links
      if (eventData.social_links) {
        payload.social_links = eventData.social_links;
      }

      // Handle ticket types
      if (eventData.ticket_types && Array.isArray(eventData.ticket_types)) {
        payload.ticket_types = eventData.ticket_types.filter(t => t.name && t.price);
      }

      console.log('📤 Final payload:', payload);

      const response = await api.post('/admin/events/', payload);
      console.log('✅ Event creation successful:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Event creation failed:', error);
      
      if (error.response) {
        console.error('Server error response:', error.response.data);
        console.error('Server error status:', error.response.status);
        
        let errorMessage = 'Event creation failed';
        if (error.response.data) {
          if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (typeof error.response.data === 'object') {
            // Show validation errors if available
            if (error.response.data.errors) {
              errorMessage = Object.entries(error.response.data.errors)
                .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
                .join('; ');
            } else {
              errorMessage = JSON.stringify(error.response.data, null, 2);
            }
          }
        }
        
        throw new Error(errorMessage);
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },
  
  adminUpdateEvent: (eventId, data) => 
    api.put(`/admin/events/${eventId}/`, data).then(res => res.data).catch(error => {
      console.error(`Error updating event ${eventId}:`, error);
      throw error;
    }),
  
  adminUpdateStatus: async (eventId, status, reason = '') => {
    try {
      const data = { status };
      if (reason) {
        data.reason = reason;
      }
      
      const response = await api.patch(`/admin/events/${eventId}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating event status ${eventId}:`, error);
      throw error;
    }
  },
  
  getEvent: (id) => 
    api.get(`/admin/events/${id}/`).then(res => res.data).catch(error => {
      console.error(`Error fetching event ${id}:`, error);
      throw error;
    }),
  
  deleteEvent: async (eventId) => {
    try {
      const response = await api.delete(`/admin/events/${eventId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting event ${eventId}:`, error);
      throw error;
    }
  },
  
  // Ticket management
  generateTickets: async (eventId, quantity, ticketTypeId = null) => {
    try {
      const data = { quantity: parseInt(quantity) };
      if (ticketTypeId) {
        data.ticket_type_id = ticketTypeId;
      }
      
      const response = await api.post(`/admin/events/${eventId}/generate-tickets/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error generating tickets for event ${eventId}:`, error);
      throw error;
    }
  },
  
   getTicketStats: (eventId) => 
    api.get(`/admin/tickets/${eventId}/stats/`).then(res => res.data).catch(error => {
      console.error(`Error fetching ticket stats for event ${eventId}:`, error);
      throw error;
    }),

     exportTickets: (eventId, format = 'csv') => 
    api.get(`/admin/tickets/${eventId}/export/?format=${format}`, { 
      responseType: 'blob' 
    }).then(res => res.data),
  
  // Add this function for redeeming tickets (admin):
  adminRedeemTicket: (eventId, ticketCode) => 
    api.post('/admin/tickets/redeem/', {
      event_id: eventId,
      ticket_code: ticketCode
    }).then(res => res.data),
  
  getTickets: (eventId) => 
    api.get(`/events/${eventId}/tickets/`).then(res => res.data).catch(error => {
      console.error(`Error fetching tickets for event ${eventId}:`, error);
      throw error;
    }),
  
  redeemTicket: async (eventId, ticketCode) => {
    try {
      const response = await api.post(`/events/${eventId}/redeem-ticket/`, { ticket_code: ticketCode });
      return response.data;
    } catch (error) {
      console.error(`Error redeeming ticket for event ${eventId}:`, error);
      throw error;
    }
  },
  
  // Public endpoints
  getUpcomingEvents: () => 
    api.get('/events/upcoming/').then(res => res.data).catch(error => {
      console.error('Error fetching upcoming events:', error);
      return [];
    }),
  
  getEventTickets: (eventId) => 
    api.get(`/admin/events/${eventId}/tickets/`).then(res => res.data).catch(error => {
      console.error(`Error fetching event tickets ${eventId}:`, error);
      throw error;
    }),

     getTickets: (eventId) => 
    api.get(`/admin/events/${eventId}/tickets/`).then(res => res.data).catch(error => {
      console.error(`Error fetching tickets for event ${eventId}:`, error);
      throw error;
    }),
  
  // Get users for host selection
  getUsers: () => 
    api.get('/users/?user_type__in=admin,staff').then(res => res.data).catch(error => {
      console.error('Error fetching users:', error);
      return [];
    }),

     getTicketDetails: (ticketCode) => 
    api.get(`/tickets/${ticketCode}/`).then(res => res.data),
  
  generateTicketImage: (ticketCode) => 
    api.post('/tickets/generate-image/', { ticket_code: ticketCode }).then(res => res.data),
  
  getUserTickets: () => 
    api.get('/tickets/my-tickets/').then(res => res.data),
  generateVisualTickets: (eventId, data) => 
    api.post(`/admin/events/${eventId}/generate-visual-tickets/`, data).then(res => res.data),
    
};

export const digitalOceanService = {
  // Generate upload URLs - UPDATED
  generateUploadURL: (data) => 
    api.post('/upload/generate-url/', {
      filename: data.filename,
      filetype: data.filetype || 'general'  // Add filetype with default
    }).then(res => res.data),
  
   generateBookCoverUploadURL: (data) => 
    api.post('/upload/book-cover/', data).then(res => res.data),
  
  generateBookPDFUploadURL: (data) => 
    api.post('/upload/book-pdf/', data).then(res => res.data),
  
  verifyUpload: (data) => 
    api.post('/upload/verify/', data).then(res => res.data),
  
  generateMusicCoverUploadURL: (data) => 
    api.post('/upload/music-cover/', data).then(res => res.data),
  
  generateMusicAudioUploadURL: (data) => 
    api.post('/upload/music-audio/', {
      filename: data.filename,
      audio_type: data.audioType || 'full'
    }).then(res => res.data),
  
  generateEventCoverUploadURL: (data) => 
    api.post('/upload/event-cover/', data).then(res => res.data),

  // Direct upload to DO Spaces
  uploadToSpaces: async (url, fields, file) => {
    const formData = new FormData();
    
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });
    
    formData.append('file', file);
    
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    
    if (response.status === 204 || response.status === 200) {
      return { success: true };
    } else {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
  }
};

export const authService = {
  login: (credentials) => api.post('/users/login/', credentials).then(res => res.data),
  getCurrentUser: () => api.get('/users/me/').then(res => res.data),
  logout: () => api.post('/users/logout/'),
};

export const booksService = {
  getAll: (params = {}) => {
  // Check if user is admin and use different endpoint
  const token = localStorage.getItem('admin_token');
  if (token) {
    return api.get('/admin/books/', { params }).then(res => res.data);
  }
  return api.get('/books/', { params }).then(res => res.data);
},
  getPending: () => api.get('/admin/pending_books/').then(res => res.data),
  
  updateBookStatus: async (bookId, status) => {
    const response = await api.put(`/author/books/${bookId}/update_status/`, { status });
    return response.data;
  },
  
  // If you also need to approve/reject with reason (admin endpoints)
  approveBook: async (bookId, reason = '') => {
    const response = await api.post(`/admin/books/${bookId}/approve/`, { reason });
    return response.data;
  },
  
  rejectBook: async (bookId, reason = '') => {
    const response = await api.post(`/admin/books/${bookId}/reject/`, { reason });
    return response.data;
  },
  
  // For admin status updates
  adminUpdateStatus: async (bookId, status, reason = '') => {
    try {
      // Option 1: Use the admin book update endpoint
      const response = await api.put(`/admin/books/${bookId}/update/`, { 
        status: status,
        ...(reason && { rejection_reason: reason }) // Include reason if provided
      });
      return response.data;
    } catch (error) {
      console.error('Admin status update failed, trying alternative...', error);
      
      // Option 2: Try the specific approve/reject endpoints
      if (status === 'approved') {
        const response = await api.post(`/admin/books/${bookId}/approve/`, { reason });
        return response.data;
      } else if (status === 'rejected') {
        const response = await api.post(`/admin/books/${bookId}/reject/`, { reason });
        return response.data;
      } else if (status === 'draft') {
        // For "put on hold" - use author endpoint
        return await booksService.updateBookStatus(bookId, 'draft');
      }
      throw error;
    }
  },

  getBook: (id) => api.get(`/books/${id}/`).then(res => res.data),
  getBookPages: (bookId) => api.get(`/books/${bookId}/pages/`).then(res => res.data),
  getGenres: () => api.get('/genres/').then(res => res.data),
  adminUploadBook: (bookData) => 
    api.post('/admin/books/upload/direct/', bookData).then(res => res.data),
  extractBookPages: async (bookId) => {
    const response = await axios.post(`/admin/books/${bookId}/extract/`);
    return response.data;
  },

  triggerReprocessing: async (bookId) => {
    const response = await axios.post(`/admin/books/${bookId}/reprocess-pages/`);
    return response.data;
  },
  updateBook: (bookId, data) => 
    api.put(`/admin/books/${bookId}/update/`, data).then(res => res.data),
  
deleteBook: (bookId) => 
  api.delete(`/admin/books/${bookId}/delete/`).then(res => res.data),
  
  replaceBookFile: (bookId, fileType, fileUrl) => 
    api.post(`/admin/books/${bookId}/replace-file/`, {
      type: fileType,
      file_url: fileUrl
    }).then(res => res.data),
};

export const usersService = {
  // Get all users with filters
  getAll: (params = {}) => 
    api.get('/users/', { params }).then(res => res.data),
  
  // Get single user
  getUser: (id) => 
    api.get(`/users/${id}/`).then(res => res.data),
  
  // Create user (staff account)
  createUser: (data) => 
    api.post('/users/', data).then(res => res.data),
  
  // Update user
  updateUser: (id, data) => 
    api.patch(`/users/${id}/`, data).then(res => res.data),
  
  // Delete user
  deleteUser: (id) => 
    api.delete(`/users/${id}/`),
  
  // Get user statistics
  getStats: () => 
    api.get('/users/stats/').then(res => res.data),
  
  // Admin-specific endpoints
 createStaff: (data) => 
    api.post('/admin-users/create-staff/', data).then(res => res.data),
  
  promoteToAdmin: (userId) => 
    api.post(`/admin-users/${userId}/promote/`).then(res => res.data),
  
  demoteToStaff: (userId) => 
    api.post(`/admin-users/${userId}/demote/`).then(res => res.data),
  
  toggleActive: (userId) => 
    api.post(`/admin-users/${userId}/toggle-active/`).then(res => res.data),
};

export const salesService = {
  getStats: () => api.get('/admin/dashboard_stats/').then(res => res.data),
  getPurchases: (params = {}) => api.get('/purchases/', { params }).then(res => res.data),
};

export const payoutsService = {
  // Pending author payouts (book earnings)
  getPending: () => api.get('/admin/pending_payouts/').then(res => res.data),

  // Process a payout for a specific author (admin triggers MoMo transfer)
  processPayout: (authorId, amount) =>
    api.post('/enhanced-admin/process_payout/', { author_id: authorId, amount })
      .then(res => res.data),

  // Get payout history
  getPayoutHistory: (params = {}) =>
    api.get('/admin/payout_history/', { params }).then(res => res.data),

  // Get pending withdrawal requests (creator self-service requests)
  getPendingWithdrawals: () =>
    api.get('/admin/payout_history/', { params: { status: 'pending' } })
      .then(res => res.data),

  // Mark a payout as completed (after manual MoMo transfer)
  markPayoutCompleted: (payoutId, momoTransactionId = '') =>
    api.post('/enhanced-admin/mark_payout_completed/', {
      payout_id: payoutId,
      momo_transaction_id: momoTransactionId,
    }).then(res => res.data),

  // Get music earnings breakdown
  getMusicEarnings: () =>
    api.get('/enhanced-admin/music_earnings_summary/').then(res => res.data),
};

export const serviceService = {
  // Get service requests
  getServiceRequests: (type = 'all', filters = {}) => 
    api.get('/admin-services/requests/', { 
      params: { 
        type,
        ...filters 
      } 
    }).then(res => res.data),
    
  // Get service statistics
  getServiceStats: () => 
    api.get('/admin-services/stats/').then(res => res.data),
    
  // Send quote to author
  sendQuote: (requestId, requestType, quoteData) =>
    api.post('/admin-services/send-quote/', {
      request_id: requestId,
      request_type: requestType,
      ...quoteData
    }).then(res => res.data),
    
  // Assign request to admin
  assignRequest: (requestId, requestType) =>
    api.post('/admin-services/assign/', {
      request_id: requestId,
      type: requestType
    }).then(res => res.data),
    
  // Update request status
  updateRequestStatus: (requestId, requestType, statusData) =>
    api.post('/admin-services/update-status/', {
      request_id: requestId,
      type: requestType,
      ...statusData
    }).then(res => res.data),
    
  // Upload audiobook file
  uploadAudiobook: (requestId, file) => {
    const formData = new FormData();
    formData.append('audiobook_file', file);
    formData.append('request_id', requestId);
    return api.post('/admin-services/upload-audiobook/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Update shipping info
  updateShippingInfo: (requestId, shippingData) =>
    api.post('/admin-services/update-shipping/', {
      request_id: requestId,
      ...shippingData
    }).then(res => res.data),
};

export const voucherService = {
  getAll: (params = {}) => api.get('/vouchers/', { params }).then(res => res.data),
  create: (data) => api.post('/vouchers/', data).then(res => res.data),
  redeem: (code) => api.post('/vouchers/redeem/', { code }).then(res => res.data),
  deactivate: (voucherId) => api.post(`/vouchers/${voucherId}/deactivate/`).then(res => res.data),
  getArtists: () => api.get('/music/artists/').then(res => {
    if (Array.isArray(res.data)) return res.data;
    if (res.data?.results) return res.data.results;
    return [];
  }),
  getArtistMusic: (artistId) => api.get(`/music/artists/${artistId}/music/`).then(res => res.data),
  adminUpdateArtist: (artistId, data) => api.post(`/music/artists/${artistId}/admin_update/`, data).then(res => res.data),
  generateUploadUrl: (filename, filetype) => api.post('/upload/generate-url/', { filename, filetype }).then(res => res.data),
};

export const enhancedAdminService = {
  processPayout: (authorId, amount) =>
    api.post('/enhanced-admin/process_payout/', { author_id: authorId, amount }).then(res => res.data),
  getAuthorEarningsDetail: (authorId) =>
    api.get(`/enhanced-admin/author_earnings_detail/?author_id=${authorId}`).then(res => res.data),
  getMusicEarnings: () =>
    api.get('/enhanced-admin/music_earnings_summary/').then(res => res.data),
  markPayoutCompleted: (payoutId, momoTransactionId = '') =>
    api.post('/enhanced-admin/mark_payout_completed/', { payout_id: payoutId, momo_transaction_id: momoTransactionId }).then(res => res.data),
  getPendingAllPayouts: () =>
    api.get('/enhanced-admin/pending_all_payouts/').then(res => res.data),
};

export const supportService = {
  // Get support tickets
  getTickets: (params = {}) => 
    api.get('/admin/support_tickets/', { params }).then(res => res.data),
    
  // Update ticket status
  updateTicketStatus: (ticketId, status, notes = '') =>
    api.post('/admin/update_ticket_status/', {
      ticket_id: ticketId,
      status,
      resolution_notes: notes
    }).then(res => res.data),
    
  // Assign ticket to admin
  assignTicket: (ticketId, adminId) =>
    api.post('/admin/assign_ticket/', {
      ticket_id: ticketId,
      admin_id: adminId
    }).then(res => res.data),
};

// services/isbnService.js or add to existing api service
export const isbnService = {
  // Admin functions
  getISBNAllRequests: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.assignedToMe) params.append('assigned_to_me', 'true');
    
    const response = await fetch(`/admin/isbn/requests/?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch ISBN requests');
    return response.json();
  },
  
  getISBNStats: async () => {
    const response = await fetch('/admin/isbn/stats/');
    if (!response.ok) throw new Error('Failed to fetch ISBN statistics');
    return response.json();
  },
  
  assignISBNRequest: async (requestId) => {
    const response = await fetch('/admin/isbn/assign/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_id: requestId }),
    });
    if (!response.ok) throw new Error('Failed to assign request');
    return response.json();
  },
  
  updateISBNRequest: async (requestId, data) => {
    const response = await fetch('/admin/isbn/update/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_id: requestId, ...data }),
    });
    if (!response.ok) throw new Error('Failed to update request');
    return response.json();
  },
  
  // Author functions
  requestISBN: async (data) => {
    const response = await fetch('/isbn/request/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to request ISBN');
    return response.json();
  },
  
  initializeISBNPayment: async (data) => {
    const response = await fetch('/isbn/payment/initialize/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to initialize payment');
    return response.json();
  },
  
  getMyISBNRequests: async () => {
    const response = await fetch('/isbn/requests/');
    if (!response.ok) throw new Error('Failed to fetch my ISBN requests');
    return response.json();
  },
  
  verifyISBNPayment: async (reference) => {
    const response = await fetch('/isbn/payment/verify/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) throw new Error('Failed to verify payment');
    return response.json();
  },
};

export const legalBusinessService = {
  // Creator endpoints
  createRequest: (data) => 
    api.post('/legal-business/requests/', data).then(res => res.data),
  
  getMyRequests: () => 
    api.get('/legal-business/my-requests/').then(res => res.data),
  
  getRequest: (id) => 
    api.get(`/legal-business/requests/${id}/`).then(res => res.data),
  
  initializePayment: (requestId, email, channel = null) => 
    api.post(`/legal-business/requests/${requestId}/initialize_payment/`, {
      request_id: requestId,
      email,
      channel
    }).then(res => res.data),
  
  verifyPayment: (reference) => 
    api.post('/legal-business/verify-payment/', { reference }).then(res => res.data),
  
  sendMessage: (requestId, message, attachmentUrl = null, attachmentName = null) => 
    api.post(`/legal-business/requests/${requestId}/send_message/`, {
      message,
      attachment_url: attachmentUrl,
      attachment_name: attachmentName
    }).then(res => res.data),
  
  getMessages: (requestId) => 
    api.get(`/legal-business/requests/${requestId}/messages/`).then(res => res.data),
  
  getNotifications: () => 
    api.get('/legal-business/notifications/').then(res => res.data),
  
  markNotificationsRead: (notificationIds = []) => 
    api.post('/legal-business/mark_notifications_read/', { notification_ids: notificationIds }).then(res => res.data),
  
  // Admin endpoints
  adminGetRequests: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
    if (filters.search) params.append('search', filters.search);
    
    return api.get(`/admin/legal-business/requests/?${params.toString()}`).then(res => res.data);
  },
  
  adminGetStats: () => 
    api.get('/admin/legal-business/stats/').then(res => res.data),
  
  adminAssignRequest: (requestId, adminId = null) => 
    api.post('/admin/legal-business/assign/', {
      request_id: requestId,
      admin_id: adminId
    }).then(res => res.data),
  
  adminUpdateRequest: (requestId, data) => 
    api.post('/admin/legal-business/update/', {
      request_id: requestId,
      ...data
    }).then(res => res.data),
  
  adminGetAvailableAdmins: (type = null) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    return api.get(`/admin/legal-business/available-admins/?${params.toString()}`).then(res => res.data);
  }
};

export default api;